import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Permission, hasPermission, type CommunityDetail, type CommunityStructure, type Member, type Page } from "@atrium/shared";
import { api, qs } from "../../lib/api";

export const useStructure = (communityId: string | undefined) =>
  useQuery({
    queryKey: ["structure", communityId],
    queryFn: () => api.get<CommunityStructure>(`/api/communities/${communityId}/structure`),
    enabled: !!communityId,
    staleTime: 60_000,
  });

export const useCommunity = (idOrSlug: string | undefined) =>
  useQuery({
    queryKey: ["community", idOrSlug],
    queryFn: () => api.get<CommunityDetail>(`/api/communities/${idOrSlug}`),
    enabled: !!idOrSlug,
  });

/** Pass `channelId` to list only the members who can see that channel. */
export const useMembers = (communityId: string | undefined, enabled = true, channelId?: string) =>
  useInfiniteQuery({
    queryKey: channelId ? ["members", communityId, channelId] : ["members", communityId],
    queryFn: ({ pageParam }) =>
      api.get<Page<Member>>(`/api/communities/${communityId}/members${qs({ cursor: pageParam as string | undefined, limit: 200, channelId })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (l) => l.nextCursor ?? undefined,
    enabled: !!communityId && enabled,
    staleTime: 60_000,
  });

/** Client-side permission hints. The server enforces every one of these independently. */
export function permsOf(perms: number, isOwner: boolean) {
  const has = (f: number) => isOwner || hasPermission(perms, f);
  return {
    manageCommunity: has(Permission.MANAGE_COMMUNITY),
    manageChannels: has(Permission.MANAGE_CHANNELS),
    manageRoles: has(Permission.MANAGE_ROLES),
    manageMessages: has(Permission.MANAGE_MESSAGES),
    kick: has(Permission.KICK_MEMBERS),
    ban: has(Permission.BAN_MEMBERS),
    invite: has(Permission.CREATE_INVITES),
    publish: has(Permission.PUBLISH_TO_FEED),
    send: has(Permission.SEND_MESSAGES),
    attach: has(Permission.ATTACH_FILES),
    react: has(Permission.ADD_REACTIONS),
    mentionEveryone: has(Permission.MENTION_EVERYONE),
    voice: has(Permission.CONNECT_VOICE),
    speak: has(Permission.SPEAK),
    admin: has(Permission.ADMINISTRATOR),
    auditLog: has(Permission.VIEW_AUDIT_LOG),
    manageInvites: has(Permission.MANAGE_INVITES),
    pin: has(Permission.PIN_MESSAGES),
    bypassSlowMode: has(Permission.BYPASS_SLOW_MODE),
    postAnnouncements: has(Permission.POST_ANNOUNCEMENTS),
    timeout: has(Permission.TIMEOUT_MEMBERS),
    muteMembers: has(Permission.MUTE_MEMBERS),
    disconnectMembers: has(Permission.DISCONNECT_MEMBERS),
    readHistory: has(Permission.READ_MESSAGE_HISTORY),
    anyAdmin:
      isOwner ||
      [
        Permission.MANAGE_COMMUNITY,
        Permission.MANAGE_ROLES,
        Permission.MANAGE_CHANNELS,
        Permission.BAN_MEMBERS,
        Permission.KICK_MEMBERS,
        Permission.VIEW_AUDIT_LOG,
        Permission.MANAGE_INVITES,
        Permission.TIMEOUT_MEMBERS,
      ].some((f) => hasPermission(perms, f)),
  };
}
export type Perms = ReturnType<typeof permsOf>;
